<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Dashboard Overview</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Track your business performance and analytics</p>
      </div>
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="reloadAll"
      >
        Refresh Data
      </v-btn>
    </div>

    <!-- Date Range Filter -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
        <span class="text-body-2 font-weight-medium text-medium-emphasis mr-4">Date Range:</span>

        <v-menu v-model="menuStart" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(startDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="startDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <span class="text-body-2 text-medium-emphasis">to</span>

        <v-menu v-model="menuEnd" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(endDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="endDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <v-divider vertical class="mx-2 d-none d-sm-block" style="height: 24px;"></v-divider>

        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          density="compact"
          variant="outlined"
          divided
          rounded="lg"
        >
          <v-btn value="7" size="small" @click="setLastDays(7)">7 Days</v-btn>
          <v-btn value="30" size="small" @click="setLastDays(30)">30 Days</v-btn>
          <v-btn value="90" size="small" @click="setLastDays(90)">90 Days</v-btn>
          <v-btn value="all" size="small" @click="setFromBeginning">From the beginning</v-btn>
        </v-btn-toggle>
      </div>
    </v-card>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="4">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Total Quotes</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.quotes || 0 }}</h3>
            </div>
            <v-avatar color="primary" variant="tonal" size="56">
              <v-icon size="28">mdi-file-document-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="success" size="18" class="mr-1">mdi-trending-up</v-icon>
            <span class="text-caption text-success font-weight-medium">In selected period</span>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="4">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Comeback Quotes</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.comebackQuotes || 0 }}</h3>
            </div>
            <v-avatar color="success" variant="tonal" size="56">
              <v-icon size="28">mdi-redo-variant</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center" v-if="stats.comebackPercentage > 0">
            <v-chip size="x-small" color="success" variant="flat" class="mr-2">
              {{ stats.comebackPercentage }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total quotes</span>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="4">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Customers</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.customers || 0 }}</h3>
            </div>
            <v-avatar color="info" variant="tonal" size="56">
              <v-icon size="28">mdi-account-group-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="info" size="18" class="mr-1">mdi-account-plus</v-icon>
            <span class="text-caption text-info font-weight-medium">Active customers</span>
          </div>
        </v-card>
      </v-col>

    </v-row>

    <!-- Charts Row 1: Timelines -->
    <v-row class="mb-6">
      <v-col cols="12" lg="6">
        <QuotesByMonthChart />
      </v-col>
      <v-col cols="12" lg="6">
        <QuotesTimelineApex :start-date="startDate" :end-date="endDate" />
      </v-col>
    </v-row>

    <!-- Charts Row 2: Occasion + Channel & Breakdown tables -->
    <v-row class="mb-6">
      <v-col cols="12" lg="6">
        <OccasionBarChart :start-date="startDate" :end-date="endDate" />
      </v-col>
      <v-col cols="12" lg="6">
        <div class="mb-6">
          <ChannelComparisonTable :start-date="startDate" :end-date="endDate" />
        </div>
        <div class="mb-6">
          <MenuBreakdownTable :start-date="startDate" :end-date="endDate" />
        </div>
        <div class="mb-6">
          <DeliveryBreakdownTable :start-date="startDate" :end-date="endDate" />
        </div>
      </v-col>
      <v-col cols="12" lg="4" gap="4">
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { quoteService, customerService, statisticsService } from "../services";
import { useNotificationStore } from "../stores";
import QuotesTimelineApex from "../components/QuotesTimelineApex.vue";
import ChannelComparisonTable from "../components/ChannelComparisonTable.vue";
import OccasionBarChart from "../components/OccasionBarChart.vue";
import MenuBreakdownTable from "../components/MenuBreakdownTable.vue";
import DeliveryBreakdownTable from "../components/DeliveryBreakdownTable.vue";
import QuotesByMonthChart from "../components/QuotesByMonthChart.vue";

export default {
  name: "DashboardView",
  components: {
    QuotesTimelineApex,
    ChannelComparisonTable,
    OccasionBarChart,
    MenuBreakdownTable,
    DeliveryBreakdownTable,
    QuotesByMonthChart,
  },
  setup() {
    const notificationStore = useNotificationStore();
    return { notificationStore };
  },
  data() {
    return {
      loading: false,
      period: "month",
      selectedPeriod: "30",
      startDate: new Date(new Date().getTime() - 29 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
      menuStart: false,
      menuEnd: false,
      stats: {
        quotes: 0,
        comebackQuotes: 0,
        comebackPercentage: 0,
        customers: 0,
        orders: 0,
      },
      recentQuotes: [],
      recentCustomers: [],
    };
  },
  methods: {
    toYmd(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    async loadDashboardData() {
      this.loading = true;
      try {
        const summaryResponse = await statisticsService.getDashboardSummary({
          startIso: this.toYmd(this.startDate),
          endIso: this.toYmd(this.endDate)
        });
        if (summaryResponse.success) {
          this.stats.quotes = summaryResponse.data.totalQuotes;
          this.stats.customers = summaryResponse.data.totalCustomers;
        }

        const comebackResponse = await statisticsService.getComebackOfferStatistics({
          period: 'custom',
          startIso: this.toYmd(this.startDate),
          endIso: this.toYmd(this.endDate)
        });
        if (comebackResponse.success) {
          this.stats.comebackQuotes = comebackResponse.data.summary.quotesWithComebackOffers;
          this.stats.comebackPercentage = this.stats.quotes > 0
            ? ((this.stats.comebackQuotes / this.stats.quotes) * 100).toFixed(1)
            : 0;
        }

        const quotesResponse = await quoteService.getQuoteEntries({ page: 1, limit: 5 });
        this.recentQuotes = quotesResponse.data || quotesResponse || [];

        const customersResponse = await customerService.list({ page: 1, limit: 5 });
        this.recentCustomers = customersResponse.data || customersResponse || [];
      } catch (error) {
        this.notificationStore.handleApiError(error, "Failed to load dashboard data");
        this.recentQuotes = [];
        this.recentCustomers = [];
      } finally {
        this.loading = false;
      }
    },
    formatDisplay(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    onDateChange() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.endDate = this.startDate;
      }
      this.reloadAll();
    },
    setLastDays(days) {
      const end = new Date();
      const start = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
      this.startDate = start;
      this.endDate = end;
      this.selectedPeriod = String(days);
      this.reloadAll();
    },
    setFromBeginning() {
      this.startDate = new Date(2025, 7, 18); // August 18th 2025
      this.endDate = new Date();
      this.selectedPeriod = 'all';
      this.reloadAll();
    },
    async reloadAll() {
      await this.loadDashboardData();
    }
  },
  mounted() {
    this.loadDashboardData();
  },
};
</script>

<style scoped>
.stat-card {
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}
</style>
